
import React, { useState } from "react";
import { Card } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { motion } from "framer-motion";
import { ChevronDown, ChevronUp, Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import ShareButtons from "@/components/ShareButtons";
import { useAuth } from "@/providers/AuthProvider";
import { toast } from "@/hooks/use-toast";
import { ScriptureResponse } from "@/hooks/useFindScripture";
import { CreateImageButton } from "@/components/VerseImageCreator";

interface ScriptureCardProps {
  scripture: ScriptureResponse;
  loadingState?: {
    commentary: boolean;
    guidance?: boolean;
  };
  onGetCommentary: () => void;
  onGetGuidance?: () => void;
  onSaveItem: (type: 'verse' | 'commentary' | 'guidance', scripture: ScriptureResponse) => void;
  hideGuidance?: boolean;
}

const ScriptureCard = ({
  scripture,
  loadingState,
  onGetCommentary,
  onGetGuidance,
  onSaveItem,
  hideGuidance = false,
}: ScriptureCardProps) => {
  const { user } = useAuth();
  const [showCommentary, setShowCommentary] = useState(false);
  const [showGuidance, setShowGuidance] = useState(false);

  const handleSaveClick = (type: 'verse' | 'commentary' | 'guidance') => {
    if (!user) {
      toast({
        title: "Authentication Required",
        description: "Please sign in to save content.",
        variant: "destructive",
      });
      return;
    }
    onSaveItem(type, scripture);
  };

  const handleCommentaryClick = () => {
    if (!scripture.commentary) {
      onGetCommentary();
    }
    setShowCommentary(!showCommentary);
  };

  const handleGuidanceClick = () => {
    if (!scripture.guidance && onGetGuidance) {
      onGetGuidance();
    }
    setShowGuidance(!showGuidance);
  };

  // Enhanced helper function to format text with paragraph breaks
  const formatText = (text: string) => {
    // Split text into paragraphs, filtering out empty lines
    const paragraphs = text.split(/\n\n+/).filter(paragraph => paragraph.trim() !== '');
    
    return paragraphs.map((paragraph, index) => (
      <p key={index} className="mb-2 text-left">{paragraph}</p>
    ));
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <Card className="bg-white border border-heaven-gold/10 shadow-md rounded-lg overflow-hidden mb-6">
        <div className="p-6 text-left">
          <h3 className="text-xl font-playfair font-semibold text-heaven-gold mb-4 text-left">
            {scripture.reference}
          </h3>
          <p className="text-2xl text-gray-700 italic mb-4 text-left font-medium">"{scripture.text}"</p>

          <div className="flex justify-between items-center mb-4">
            <div className="flex space-x-2">
              <Button
                variant="blue"
                size="sm"
                onClick={() => handleSaveClick('verse')}
                className="flex items-center gap-1"
              >
                <Save className="h-4 w-4" />
                <span>Save</span>
              </Button>
              <ShareButtons
                scripture={scripture}
                type="verse"
              />
              {/* Add Create Image Button */}
              <CreateImageButton scripture={scripture} size="sm" />
            </div>
          </div>

          <Separator className="my-4" />

          {/* Commentary Section */}
          <div 
            className="cursor-pointer hover:bg-gray-50 transition-colors rounded-lg p-2 text-left"
            onClick={handleCommentaryClick}
          >
            <div className="flex items-center justify-between">
              <h4 className="text-lg font-medium flex items-center text-heaven-gold">
                Commentary
              </h4>
              {showCommentary ? (
                <ChevronUp className="h-5 w-5 text-heaven-gold" />
              ) : (
                <ChevronDown className="h-5 w-5 text-heaven-gold" />
              )}
            </div>

            {showCommentary && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="mt-4"
              >
                {loadingState?.commentary ? (
                  <div className="py-4 text-gray-500 text-left">Loading commentary...</div>
                ) : scripture.commentary ? (
                  <>
                    <div className="prose prose-heaven max-w-none text-left">
                      <div 
                        className="text-gray-700 leading-relaxed"
                      >
                        {formatText(scripture.commentary)}
                      </div>
                    </div>
                    <div className="flex justify-between mt-4">
                      <div className="flex space-x-2">
                        <Button
                          variant="blue"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSaveClick('commentary');
                          }}
                          className="flex items-center gap-1"
                        >
                          <Save className="h-4 w-4" />
                          <span>Save</span>
                        </Button>
                        <ShareButtons
                          scripture={scripture}
                          type="commentary"
                        />
                      </div>
                    </div>
                  </>
                ) : null}
              </motion.div>
            )}
          </div>

          {!hideGuidance && (
            <>
              <Separator className="my-4" />
              
              {/* Guidance Section */}
              <div 
                className="cursor-pointer hover:bg-gray-50 transition-colors rounded-lg p-2 text-left"
                onClick={handleGuidanceClick}
              >
                <div className="flex items-center justify-between">
                  <h4 className="text-lg font-medium flex items-center text-heaven-gold">
                    Personal Guidance
                  </h4>
                  {showGuidance ? (
                    <ChevronUp className="h-5 w-5 text-heaven-gold" />
                  ) : (
                    <ChevronDown className="h-5 w-5 text-heaven-gold" />
                  )}
                </div>

                {showGuidance && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    className="mt-4"
                  >
                    {loadingState?.guidance ? (
                      <div className="py-4 text-gray-500 text-left">Loading guidance...</div>
                    ) : scripture.guidance ? (
                      <>
                        <div className="prose prose-heaven max-w-none text-left">
                          <div 
                            className="text-gray-700 leading-relaxed"
                          >
                            {formatText(scripture.guidance)}
                          </div>
                        </div>
                        <div className="flex justify-between mt-4">
                          <div className="flex space-x-2">
                            <Button
                              variant="blue"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleSaveClick('guidance');
                              }}
                              className="flex items-center gap-1"
                            >
                              <Save className="h-4 w-4" />
                              <span>Save</span>
                            </Button>
                            <ShareButtons
                              scripture={scripture}
                              type="guidance"
                            />
                          </div>
                        </div>
                      </>
                    ) : null}
                  </motion.div>
                )}
              </div>
            </>
          )}
        </div>
      </Card>
    </motion.div>
  );
};

export default ScriptureCard;
export { ScriptureCard };
